class EnhancedMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.gallery = this.querySelector('media-gallery');
    this.viewer = this.querySelector('[id^="GalleryViewer"]');
    this.thumbnailSlider = document.querySelector('thumbnail-slider');
    this.customCursor = null;
    this.rafId = null;
    this.cursorX = 0;
    this.cursorY = 0;
    this.isHovering = false;
    
    if (!this.viewer) return;
    
    this.init();
  }

  init() {
    this.createCustomCursor();
    this.createHoverNavigation();
    this.createZoomButton();
    this.setupKeyboardNavigation();
    this.setupThumbnailScrolling();
    this.startCursorAnimation();
  }

  createCustomCursor() {
    // Create custom cursor element
    this.customCursor = document.createElement('div');
    this.customCursor.className = 'custom-cursor';
    this.customCursor.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    `;
    document.body.appendChild(this.customCursor);
  }

  startCursorAnimation() {
    // Use requestAnimationFrame for smooth cursor updates
    const updateCursor = () => {
      if (this.isHovering && this.customCursor) {
        this.customCursor.style.left = this.cursorX + 'px';
        this.customCursor.style.top = this.cursorY + 'px';
      }
      this.rafId = requestAnimationFrame(updateCursor);
    };
    updateCursor();
  }

  updateCustomCursor(e, direction) {
    if (!this.customCursor) return;
    
    // Just store the coordinates, RAF will handle the update
    this.cursorX = e.clientX;
    this.cursorY = e.clientY;
    this.isHovering = true;
    
    if (!this.customCursor.classList.contains('active')) {
      this.customCursor.classList.add('active');
    }
    
    // Update arrow direction
    const svg = this.customCursor.querySelector('svg');
    if (direction === 'prev') {
      svg.innerHTML = '<polyline points="15 18 9 12 15 6"></polyline>';
    } else {
      svg.innerHTML = '<polyline points="9 18 15 12 9 6"></polyline>';
    }
  }

  hideCustomCursor() {
    this.isHovering = false;
    if (this.customCursor) {
      this.customCursor.classList.remove('active');
    }
  }

  setupThumbnailScrolling() {
    if (!this.thumbnailSlider) return;

    const thumbnailList = this.thumbnailSlider.querySelector('.thumbnail-list');
    if (!thumbnailList) return;

    // Update scroll indicators
    const updateScrollIndicators = () => {
      const container = thumbnailList;
      const hasScrollLeft = container.scrollLeft > 10;
      const hasScrollRight = container.scrollLeft < container.scrollWidth - container.clientWidth - 10;

      this.thumbnailSlider.classList.toggle('has-scroll-left', hasScrollLeft);
      this.thumbnailSlider.classList.toggle('has-scroll-right', hasScrollRight);
    };

    // Initial check
    updateScrollIndicators();

    // Update on scroll
    thumbnailList.addEventListener('scroll', updateScrollIndicators);

    // Update on resize
    window.addEventListener('resize', updateScrollIndicators);

    // Auto-scroll when clicking thumbnails
    const thumbnails = thumbnailList.querySelectorAll('.thumbnail-list__item');
    thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', (e) => {
        setTimeout(() => {
          this.scrollThumbnailIntoView(thumbnail, thumbnailList);
        }, 100);
      });
    });

    // Observe when active thumbnail changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList.contains('is-active')) {
            this.scrollThumbnailIntoView(target, thumbnailList);
          }
        }
      });
    });

    thumbnails.forEach(thumbnail => {
      observer.observe(thumbnail, { attributes: true });
    });
  }

  scrollThumbnailIntoView(thumbnail, container) {
    const thumbnailRect = thumbnail.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    const thumbnailLeft = thumbnail.offsetLeft;
    const thumbnailRight = thumbnailLeft + thumbnail.offsetWidth;
    const containerScrollLeft = container.scrollLeft;
    const containerWidth = container.clientWidth;
    
    const visibleLeft = containerScrollLeft;
    const visibleRight = containerScrollLeft + containerWidth;
    
    // Add padding for better UX (show some context)
    const padding = 80;
    
    if (thumbnailLeft < visibleLeft + padding) {
      // Thumbnail is too far left, scroll left
      container.scrollTo({
        left: Math.max(0, thumbnailLeft - padding),
        behavior: 'smooth'
      });
    } else if (thumbnailRight > visibleRight - padding) {
      // Thumbnail is too far right, scroll right
      container.scrollTo({
        left: thumbnailRight - containerWidth + padding,
        behavior: 'smooth'
      });
    }
  }

  createHoverNavigation() {
    const navContainer = document.createElement('div');
    navContainer.className = 'gallery-hover-nav';
    navContainer.innerHTML = `
      <button type="button" class="gallery-hover-nav__button gallery-hover-nav__button--prev" aria-label="Previous image"></button>
      <button type="button" class="gallery-hover-nav__button gallery-hover-nav__button--next" aria-label="Next image"></button>
    `;

    this.viewer.appendChild(navContainer);

    const prevButton = navContainer.querySelector('.gallery-hover-nav__button--prev');
    const nextButton = navContainer.querySelector('.gallery-hover-nav__button--next');

    // Prev button events
    prevButton.addEventListener('mouseenter', () => {
      this.isHovering = true;
      this.customCursor.classList.add('active');
    });

    prevButton.addEventListener('mousemove', (e) => {
      this.updateCustomCursor(e, 'prev');
    });

    prevButton.addEventListener('mouseleave', () => {
      this.hideCustomCursor();
    });

    prevButton.addEventListener('click', () => {
      this.navigateGallery('prev');
    });

    // Next button events
    nextButton.addEventListener('mouseenter', () => {
      this.isHovering = true;
      this.customCursor.classList.add('active');
    });

    nextButton.addEventListener('mousemove', (e) => {
      this.updateCustomCursor(e, 'next');
    });

    nextButton.addEventListener('mouseleave', () => {
      this.hideCustomCursor();
    });

    nextButton.addEventListener('click', () => {
      this.navigateGallery('next');
    });
  }

  createZoomButton() {
  // Only create if zoom is enabled
  const hasZoom = this.closest('.product')?.classList.contains('product--zoom-disabled');
  if (hasZoom) return;

  const zoomButton = document.createElement('button');
  zoomButton.className = 'gallery-zoom-button';
  zoomButton.type = 'button';
  zoomButton.setAttribute('aria-label', 'Zoom image');
  zoomButton.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      <line x1="11" y1="8" x2="11" y2="14"></line>
      <line x1="8" y1="11" x2="14" y2="11"></line>
    </svg>
  `;

  // Add to the VIEWER (main gallery), not media wrapper
  // This ensures it only appears on the main image, not thumbnails
  this.viewer.style.position = 'relative';
  this.viewer.appendChild(zoomButton);

  // Don't hide cursor - just let it show normally
  // Removed the mouseenter event that was hiding the cursor

  // Trigger zoom by finding and clicking the actual image toggle
  zoomButton.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get all possible click targets in the active media
    const activeMedia = this.viewer.querySelector('.product__media-item.is-active');
    
    if (activeMedia) {
      // Find any clickable element that opens the modal
      const clickableElements = [
        activeMedia.querySelector('button.product__media-toggle'),
        activeMedia.querySelector('.product__modal-opener'),
        activeMedia.querySelector('.product__modal-opener--image'),
        activeMedia.querySelector('[data-media-id] button'),
        activeMedia.querySelector('product-gallery button')
      ];
      
      // Try each element until one works
      for (let element of clickableElements) {
        if (element) {
          console.log('Found zoom element:', element);
          element.click();
          return; // Exit after first successful click
        }
      }
      
      console.warn('No zoom element found in active media');
    }
  });
}

  navigateGallery(direction) {
    const slides = Array.from(this.viewer.querySelectorAll('.product__media-item:not(.product__media-item--variant)'));
    const activeSlide = this.viewer.querySelector('.product__media-item.is-active');
    const currentIndex = slides.indexOf(activeSlide);
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : slides.length - 1;
    } else {
      newIndex = currentIndex < slides.length - 1 ? currentIndex + 1 : 0;
    }

    const newSlide = slides[newIndex];
    const mediaId = newSlide.dataset.mediaId;
    
    // Trigger the media gallery's setActiveMedia method
    if (this.gallery) {
      this.gallery.setActiveMedia(mediaId, false, false);
    }
  }

  setupKeyboardNavigation() {
    this.viewer.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.navigateGallery('prev');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.navigateGallery('next');
      }
    });
  }

  disconnectedCallback() {
    // Clean up RAF when element is removed
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    if (this.customCursor && this.customCursor.parentNode) {
      this.customCursor.parentNode.removeChild(this.customCursor);
    }
  }
}

customElements.define('enhanced-media-gallery', EnhancedMediaGallery);

// Initialize on load
document.addEventListener('DOMContentLoaded', () => {
  // Force update scroll indicators after images load
  const thumbnailSlider = document.querySelector('thumbnail-slider');
  if (thumbnailSlider) {
    const thumbnailList = thumbnailSlider.querySelector('.thumbnail-list');
    if (thumbnailList) {
      const updateScrollIndicators = () => {
        const hasScrollLeft = thumbnailList.scrollLeft > 10;
        const hasScrollRight = thumbnailList.scrollLeft < thumbnailList.scrollWidth - thumbnailList.clientWidth - 10;
        
        thumbnailSlider.classList.toggle('has-scroll-left', hasScrollLeft);
        thumbnailSlider.classList.toggle('has-scroll-right', hasScrollRight);
      };
      
      // Update after images load
      const images = thumbnailList.querySelectorAll('img');
      let loadedCount = 0;
      images.forEach(img => {
        if (img.complete) {
          loadedCount++;
        } else {
          img.addEventListener('load', () => {
            loadedCount++;
            if (loadedCount === images.length) {
              updateScrollIndicators();
            }
          });
        }
      });
      
      if (loadedCount === images.length) {
        updateScrollIndicators();
      }
    }
  }
});